import { z } from 'zod';
import { isOrderedRange, isoDateSchema, moneySchema, ORDERED_RANGE_ISSUE, uuidSchema } from '../primitives.js';
import { DELIVERY_ORIGINS } from './deliveries.js';

/** Who received a delivery's fee — see apps/api finance/custody.ts. */
export const DELIVERY_FEE_CUSTODY_BASES = [
  'rider_collection',
  'other_collector',
  'counter_receipt',
  'company_counter',
  'company_payment',
  'unconfirmed',
  'unpaid',
] as const;
export type DeliveryFeeCustodyBasis = (typeof DELIVERY_FEE_CUSTODY_BASES)[number];

/**
 * Finance: what riders earned, and what the company earned.
 *
 * Two screens, each answering one question from the records that hold the
 * answer:
 *
 *   Rider earnings    per day and rider, from each completed delivery's OWN
 *                     stored fee and payout — never today's rate applied to
 *                     an old ride.
 *   Company earnings  delivery commission plus remittance received, less
 *                     maintenance: "Earnings after maintenance". Not net
 *                     profit — rent, salaries and every other expense are
 *                     not deducted, and the label must never suggest they are.
 *
 * Earned and collected are kept apart everywhere. A ride is earned on the day
 * it was done; the customer's money arriving on a later day moves cash on
 * that later day and never moves the ride or creates a second revenue entry.
 *
 * Money in different currencies is never added together: every total is per
 * currency, because no conversion basis has been chosen.
 */

// ── Rider earnings ─────────────────────────────────────────────────────────

export const riderEarningsQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    riderId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    country: z.string().trim().length(2).optional(),
    /** A state code, or `__unset__` for locations that name none. */
    state: z.string().trim().min(1).max(40).optional(),
    source: z.enum(DELIVERY_ORIGINS).optional(),
  })
  .refine((v) => isOrderedRange(v), { message: ORDERED_RANGE_ISSUE.message, path: ['from'] });
export type RiderEarningsQuery = z.infer<typeof riderEarningsQuerySchema>;

export const riderEarningsRowSchema = z.object({
  date: z.string(),
  /** Null for a name no rider record covers yet. */
  riderId: uuidSchema.nullable(),
  riderName: z.string(),
  currencyCode: z.string(),
  completedRides: z.number().int(),
  grossDeliveryFees: moneySchema,
  /** The payout stored on each delivery when it was costed. */
  riderEarnings: moneySchema,
  /** Stored commission per delivery: booked company revenue, else fee − payout. */
  companyCommission: moneySchema,
  /** Of the rider's earnings, what a paid settlement has actually paid them. */
  riderPaid: moneySchema,
  /** Of the fees, what the customer has actually paid. */
  customerCollected: moneySchema,
  /** Of the fees, what the rider received and holds for the company (custody.ts): it reduces what they are owed. */
  cashToRider: moneySchema,
  /** Rides marked paid in cash with nobody recorded as receiving it: not deducted, for reconciliation. */
  uncertainCashRides: z.number().int(),
  uncertainCashFees: moneySchema,
});
export type RiderEarningsRow = z.infer<typeof riderEarningsRowSchema>;

export const riderEarningsTotalsSchema = riderEarningsRowSchema
  .omit({ date: true, riderId: true, riderName: true })
  .extend({ riders: z.number().int(), days: z.number().int() });
export type RiderEarningsTotals = z.infer<typeof riderEarningsTotalsSchema>;

export const riderEarningsReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  rows: z.array(riderEarningsRowSchema),
  /** One total per currency; never summed across currencies. */
  totals: z.array(riderEarningsTotalsSchema),
});
export type RiderEarningsReport = z.infer<typeof riderEarningsReportSchema>;

export const riderEarningsDeliveriesQuerySchema = z.object({
  date: isoDateSchema,
  riderId: uuidSchema.optional(),
  /** For a name no rider record covers. */
  riderName: z.string().trim().min(1).max(200).optional(),
  branchId: uuidSchema.optional(),
  country: z.string().trim().length(2).optional(),
  state: z.string().trim().min(1).max(40).optional(),
  source: z.enum(DELIVERY_ORIGINS).optional(),
});
export type RiderEarningsDeliveriesQuery = z.infer<typeof riderEarningsDeliveriesQuerySchema>;

export const financeDeliverySchema = z.object({
  id: uuidSchema,
  reference: z.string().nullable(),
  date: z.string(),
  riderName: z.string(),
  source: z.string(),
  branchName: z.string().nullable(),
  currencyCode: z.string(),
  fee: moneySchema,
  riderPayout: moneySchema,
  commission: moneySchema,
  paymentStatus: z.string(),
  deliveryStatus: z.string(),
});
export type FinanceDelivery = z.infer<typeof financeDeliverySchema>;

// ── Company earnings ───────────────────────────────────────────────────────

export const COMPANY_EARNINGS_PERIODS = ['day', 'week', 'month', 'year', 'custom'] as const;
export type CompanyEarningsPeriod = (typeof COMPANY_EARNINGS_PERIODS)[number];

export const companyEarningsQuerySchema = z
  .object({
    period: z.enum(COMPANY_EARNINGS_PERIODS).default('month'),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    branchId: uuidSchema.optional(),
  })
  .refine((v) => v.period !== 'custom' || (v.from !== undefined && v.to !== undefined), {
    message: 'A custom range needs both dates',
    path: ['from'],
  })
  .refine((v) => isOrderedRange(v), { message: ORDERED_RANGE_ISSUE.message, path: ['from'] });
export type CompanyEarningsQuery = z.infer<typeof companyEarningsQuerySchema>;

export const companyEarningsFiguresSchema = z.object({
  currencyCode: z.string(),
  /** Company share of delivery fees, counted once. Riders' share is excluded. */
  deliveryCommission: moneySchema,
  /**
   * Remittance money received in the period, by the day it arrived, whichever
   * week it paid. Cash basis: a week charged and unpaid adds nothing. Null when
   * not attributable to the view.
   */
  remittanceRevenue: moneySchema.nullable(),
  /**
   * All remittance cash received in the period, investors' shares included.
   * remittanceRevenue + investorFundsCollected.
   */
  grossRemittanceCollected: moneySchema.nullable(),
  /** Investors' shares of the remittance received: owed to them, never revenue. */
  investorFundsCollected: moneySchema.nullable(),
  /** Paid to investors in the period. Settles what is owed; never an expense. */
  investorFundsPaidOut: moneySchema.nullable(),
  /** Collected for investors and not yet paid to them, at the end of the period. */
  investorPayablesOutstanding: moneySchema.nullable(),
  /**
   * What the weeks ending in the period charged, waived weeks excluded —
   * accrued, for reference. Not revenue and not in any total here: it becomes
   * revenue when it is paid, in `remittanceRevenue`.
   */
  remittanceCharged: moneySchema.nullable(),
  /**
   * What those charged weeks still owe today: charged less the payments
   * against them (wherever those payments fall), waived and settled weeks
   * owing nothing. A balance, not revenue.
   */
  remittanceOutstanding: moneySchema.nullable(),
  /**
   * Other revenue the company earned: sales that are not delivery fees, and
   * interest and fees received on money it lent. Counted once — a delivery's
   * sale is never in here, it is in `deliveryCommission`.
   */
  otherRevenue: moneySchema,
  /** deliveryCommission + remittanceRevenue + otherRevenue. */
  totalCompanyRevenue: moneySchema,
  maintenanceExpenses: moneySchema,
  /** totalCompanyRevenue − maintenanceExpenses. Not net profit. */
  earningsAfterMaintenance: moneySchema,
  /**
   * Every other expense recorded in the period. Never riders' payouts — the
   * riders' share is already outside commission — and never maintenance,
   * which is above.
   */
  operatingExpenses: moneySchema,
  /** earningsAfterMaintenance − operatingExpenses. Before tax, loan bad debts and asset costs. */
  earningsAfterExpenses: moneySchema,
  /** Commission on completed rides the customer has not paid for yet — not counted above. */
  commissionAwaitingPayment: moneySchema,
  collections: z.object({
    /** Remittance cash received in the period — the same figure as `remittanceRevenue`. */
    remittanceReceived: moneySchema.nullable(),
    /** Delivery fees customers paid in the period, whenever the ride was. */
    deliveryFeesReceived: moneySchema,
  }),
});
export type CompanyEarningsFigures = z.infer<typeof companyEarningsFiguresSchema>;

export const companyEarningsSchema = z.object({
  period: z.enum(COMPANY_EARNINGS_PERIODS),
  from: z.string(),
  to: z.string(),
  /** The business's own currency first, then any other a location uses. */
  currencies: z.array(companyEarningsFiguresSchema),
  /** Why remittance figures are absent, when they are. */
  remittanceNote: z.string().nullable(),
});
export type CompanyEarnings = z.infer<typeof companyEarningsSchema>;

export const COMPANY_EARNINGS_KINDS = [
  'commission',
  'remittance',
  'remittance_charged',
  'remittance_outstanding',
  'maintenance',
  'remittance_collections',
  'delivery_collections',
  'other_revenue',
  'operating_expenses',
] as const;
export type CompanyEarningsKind = (typeof COMPANY_EARNINGS_KINDS)[number];

export const companyEarningsTransactionsQuerySchema = z
  .object({
    kind: z.enum(COMPANY_EARNINGS_KINDS),
    from: isoDateSchema,
    to: isoDateSchema,
    currency: z.string().trim().length(3).optional(),
    branchId: uuidSchema.optional(),
  })
  .refine((v) => isOrderedRange(v), { message: ORDERED_RANGE_ISSUE.message, path: ['from'] });
export type CompanyEarningsTransactionsQuery = z.infer<typeof companyEarningsTransactionsQuerySchema>;

export const companyEarningsTransactionSchema = z.object({
  id: uuidSchema,
  date: z.string(),
  reference: z.string().nullable(),
  description: z.string(),
  counterparty: z.string().nullable(),
  amount: moneySchema,
});
export type CompanyEarningsTransaction = z.infer<typeof companyEarningsTransactionSchema>;

export const companyEarningsTransactionsSchema = z.object({
  kind: z.enum(COMPANY_EARNINGS_KINDS),
  currencyCode: z.string(),
  /** Over every row, equal to the tile that was opened. */
  total: moneySchema,
  count: z.number().int(),
  rows: z.array(companyEarningsTransactionSchema),
  /** True when more rows matched than are listed; `total` still covers them all. */
  truncated: z.boolean(),
});
export type CompanyEarningsTransactions = z.infer<typeof companyEarningsTransactionsSchema>;

// ── Daily rider earnings ───────────────────────────────────────────────────
//
// One service date, per rider: rides, the full delivery fees they carried,
// and the rider's share split by stage — in transit (assigned, not yet
// delivered) and delivered. A report of EARNINGS, never of payment: whether
// the customer has paid and whether the rider has been paid out are separate
// facts, shown beside it, and nothing here writes to any ledger.

const dailyScope = {
  branchId: uuidSchema.optional(),
  country: z.string().trim().length(2).optional(),
  /** A state code, or `__unset__` for locations that name none. */
  state: z.string().trim().min(1).max(40).optional(),
  /** Rider name contains — narrows the cards, rows and totals alike. */
  search: z.string().trim().max(80).optional(),
};

export const dailyRiderEarningsQuerySchema = z.object({ date: isoDateSchema, ...dailyScope });
export type DailyRiderEarningsQuery = z.infer<typeof dailyRiderEarningsQuerySchema>;

export const dailyRiderEarningsDeliveriesQuerySchema = z.object({
  date: isoDateSchema,
  /** The row's key: a rider id, or `name:<NAME>` for a name no rider record covers. */
  riderKey: z.string().trim().min(1).max(260),
  currencyCode: z.string().trim().length(3).optional(),
  ...dailyScope,
});
export type DailyRiderEarningsDeliveriesQuery = z.infer<typeof dailyRiderEarningsDeliveriesQuerySchema>;

const earningsFigures = {
  rides: z.number().int(),
  inTransitRides: z.number().int(),
  deliveredRides: z.number().int(),
  /** Full delivery fees of those rides — what the customer is charged for delivery. */
  income: moneySchema,
  inTransit: moneySchema,
  delivered: moneySchema,
  /** inTransit + delivered. */
  total: moneySchema,
};

/**
 * Rider Payouts for the selected period: total earnings less cash to rider,
 * before settlement payments, returns and adjustments. See dailyRiderEarnings.ts.
 */
const payoutFigures = {
  /** Delivery-fee cash a record says the rider received on these rides. */
  cashToRider: moneySchema,
  /** total − cashToRider, never below zero. */
  payout: moneySchema,
  /** cashToRider − total when that is positive: the rider owes the company. */
  owesCompany: moneySchema,
  /** Rides the record says were paid in cash with nobody recorded as receiving it. Not deducted; for review. */
  unconfirmedCashRides: z.number().int(),
  unconfirmedCashFees: moneySchema,
};

export const dailyRiderEarningsRowSchema = z.object({
  riderKey: z.string(),
  riderId: uuidSchema.nullable(),
  riderName: z.string(),
  ...earningsFigures,
  ...payoutFigures,
});
export type DailyRiderEarningsRow = z.infer<typeof dailyRiderEarningsRowSchema>;

export const dailyRiderEarningsCurrencySchema = z.object({
  currencyCode: z.string(),
  totals: z.object({
    ...earningsFigures,
    riders: z.number().int(),
    /** The company's share of every counted ride, by the stored commission. */
    commissionAll: moneySchema,
    /** The company's share of the delivered ones. */
    commissionDelivered: moneySchema,
    cashToRider: moneySchema,
    /** Sum of each rider's own payout — debts are never netted against it. */
    payout: moneySchema,
    /** Sum of what riders owe, kept apart. */
    owesCompany: moneySchema,
    ridersOwing: z.number().int(),
    unconfirmedCashRides: z.number().int(),
    unconfirmedCashFees: moneySchema,
  }),
  riders: z.array(dailyRiderEarningsRowSchema),
  /** Left out of every figure above, and said so. */
  excluded: z.object({
    cancelledRides: z.number().int(),
    /** A payout actually recorded on a cancelled ride (a rider who rode before it was called off). */
    cancelledPayout: moneySchema,
    /** Failed or returned: not delivered, so neither column; their recorded payout is shown apart. */
    returnedRides: z.number().int(),
    returnedPayout: moneySchema,
    /** Pending / Awaiting Rider with a rider named but not accepted: no ride, no earnings yet. */
    pendingAcceptanceRides: z.number().int(),
    /** Their delivery fees, for reference — not income generated until accepted. */
    pendingAcceptanceFees: moneySchema,
    /** Jobs no rider is named on: nobody earned them yet. */
    unassignedRides: z.number().int(),
  }),
});
export type DailyRiderEarningsCurrency = z.infer<typeof dailyRiderEarningsCurrencySchema>;

export const dailyRiderEarningsReportSchema = z.object({
  date: z.string(),
  timezone: z.string(),
  currencies: z.array(dailyRiderEarningsCurrencySchema),
});
export type DailyRiderEarningsReport = z.infer<typeof dailyRiderEarningsReportSchema>;

export const dailyRiderEarningsDeliverySchema = z.object({
  id: uuidSchema,
  reference: z.string(),
  /** The customer's own order number (a WhatsApp CENO-… code), when it is not the reference. */
  orderRef: z.string().nullable(),
  /** Shop (typed at a counter or Shop Entry), App, WhatsApp — or not recorded on old imports. */
  source: z.enum(['shop', 'app', 'whatsapp', 'not_recorded']),
  serviceDate: z.string(),
  deliveryStatus: z.string().nullable(),
  stage: z.enum(['in_transit', 'delivered']),
  currencyCode: z.string(),
  fee: moneySchema,
  commission: moneySchema,
  riderEarnings: moneySchema,
  /** The customer's side, as recorded on the delivery. */
  paymentStatus: z.string().nullable(),
  /** The rider's side: paid out in a settlement, or not yet. */
  riderPayoutStatus: z.enum(['paid_out', 'not_paid_out']),
  /** Delivery-fee cash deducted from this rider's payout for this ride. */
  cashToRider: moneySchema,
  /**
   * Who received the fee, by the one custody rule (apps/api finance/custody.ts):
   * a collection recorded against this rider; another recorded collector; the
   * counter's Cash-to-Rider receipt (the rider holds it); the counter's own
   * receipt into the company; a non-cash payment to the company; cash said but
   * nobody recorded receiving it (for reconciliation); or not paid yet.
   */
  cashBasis: z.enum(DELIVERY_FEE_CUSTODY_BASES),
});
export type DailyRiderEarningsDelivery = z.infer<typeof dailyRiderEarningsDeliverySchema>;
